import { useEffect, useMemo, useState } from "react";
import { Eye, FileCheck } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import {
  API_DOCUMENT_APPROVER,
  API_DOCUMENT_LIST_APPROVER,
  API_GET_EMPLOYEE_FILE,
} from "../../../../../../config/Endpoints.js";
import DocumentViewer from "../../../../../../components/DocumentViewer.jsx";
import {
  Badge,
  Button,
  Modal,
  ResponsiveDataTable,
  Select,
  TextArea,
  type ResponsiveColumn,
  type SelectOption,
} from "../../../../ui/index.ts";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import { accessToken } from "../../../../../utils/auth.ts";
import { getApi, putApi } from "../../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import type { DocumentApprovalRow } from "../../types.ts";
import {
  extractApiMessage,
  formatDisplayDate,
  formatStatus,
  statusBadgeVariant,
} from "../../utils.ts";
import styles from "./document-approval-tab.module.css";

type ApproverEmployee = {
  employeeId?: number | string;
  employeeName?: string;
  employeeType?: string;
  documentResponseList?: Array<{
    documentType?: string;
    documentPath?: string;
    approvalStatus?: string;
    pendingFrom?: string;
    documentId?: number | string;
    documentViewId?: number | string;
  }>;
};

export function DocumentApprovalTab() {
  const [documents, setDocuments] = useState<DocumentApprovalRow[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("");
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentApprovalRow | null>(null);
  const [documentAction, setDocumentAction] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [viewLoadingId, setViewLoadingId] = useState<string | number | null>(null);
  const [documentDetail, setDocumentDetail] = useState({
    url: "",
    type: "",
    name: "",
  });

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { data } = await getApi<{ data?: ApproverEmployee[] }>(
        API_DOCUMENT_LIST_APPROVER,
        accessToken(),
      );
      const employees = Array.isArray(data?.data) ? data.data : [];
      const nextDocuments = employees.flatMap((employee) =>
        (employee.documentResponseList ?? []).map((document, index) => ({
          id: `${employee.employeeId ?? "employee"}-${document.documentId ?? index}`,
          employeeId: String(employee.employeeId ?? ""),
          employeeName: employee.employeeName || "-",
          documentType: document.documentType || "-",
          documentPath: document.documentPath,
          approvalStatus: document.approvalStatus,
          pendingFrom: document.pendingFrom,
          documentId: document.documentId,
          documentViewId: document.documentViewId,
        })),
      );

      setDocuments(nextDocuments);
      setEmployeeOptions([
        { value: "", label: "All Employees" },
        ...employees.map((employee) => {
          const type = employee.employeeType?.trim();
          return {
            value: String(employee.employeeId ?? ""),
            label: type
              ? `${employee.employeeName ?? "-"} (${type})`
              : employee.employeeName ?? "-",
          };
        }),
      ]);
    } catch (e) {
      showError(extractApiMessage(e, "Failed to load document approvals."));
      setDocuments([]);
      setEmployeeOptions([{ value: "", label: "All Employees" }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchDocuments();
  }, []);

  const documentTypeOptions = useMemo<SelectOption[]>(() => {
    const uniqueTypes = Array.from(
      new Set(
        documents
          .map((item) => item.documentType)
          .filter((value) => value && value !== "-"),
      ),
    );
    return [
      { value: "", label: "All Documents" },
      ...uniqueTypes.map((value) => ({ value, label: value })),
    ];
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    return documents
      .filter((item) => (employeeFilter ? item.employeeId === employeeFilter : true))
      .filter((item) =>
        documentTypeFilter ? item.documentType === documentTypeFilter : true,
      )
      .sort((a, b) =>
        String(a.pendingFrom ?? "").localeCompare(String(b.pendingFrom ?? "")),
      );
  }, [documentTypeFilter, documents, employeeFilter]);

  const openActionModal = (document: DocumentApprovalRow) => {
    setSelectedDocument(document);
    setDocumentAction("");
    setReason("");
  };

  const closeActionModal = () => {
    setSelectedDocument(null);
    setDocumentAction("");
    setReason("");
  };

  const clearDocumentDetail = () => {
    setDocumentDetail((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { url: "", type: "", name: "" };
    });
  };

  const handleViewDocument = async (doc: DocumentApprovalRow) => {
    if (!doc.documentPath) {
      showError("Document path is not available.");
      return;
    }

    setViewLoadingId(doc.documentId!);

    try {
      const filepath = doc.documentPath.split("/").slice(3).join("/");

      const url = new URL(API_GET_EMPLOYEE_FILE);
      url.searchParams.set("filepath", filepath);

      const response = await fetch(url.toString(), {
        headers: {
          ...(accessToken()
            ? { Authorization: `Bearer ${accessToken()}` }
            : {}),
        },
      });

      if (!response.ok) {
        throw new Error("Unable to open document.");
      }

      const contentType =
        response.headers.get("content-type")?.split(";")[0]?.trim() ||
        "application/octet-stream";

      const blob = await response.blob();

      const fileUrl = URL.createObjectURL(
        new Blob([blob], { type: contentType }),
      );

      setDocumentDetail((prev) => {
        if (prev.url) URL.revokeObjectURL(prev.url);

        return {
          url: fileUrl,
          type: contentType,
          name: doc.documentType || "Document",
        };
      });

      window.setTimeout(() => {
        window.document.getElementById("open-document-view")?.click();
      }, 0);
    } catch (e) {
      showError(extractApiMessage(e, "Unable to open document."));
    } finally {
      setViewLoadingId(null);
    }
  };

  const handleSubmitAction = async () => {
    if (!selectedDocument || !selectedDocument.documentId || !documentAction) {
      showError("Please select an action.");
      return;
    }
    if (documentAction === "rejected" && reason.trim().length < 5) {
      showError("Please enter a rejection reason.");
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        documentId: String(selectedDocument.documentId),
        status: documentAction,
      });
      if (documentAction === "rejected") {
        params.set("reason", reason.trim());
      }
      const { data } = await putApi<{ message?: string }>(
        `${API_DOCUMENT_APPROVER}?${params.toString()}`,
        accessToken(),
        {},
      );
      showSuccess(data?.message || "Document status updated successfully.");
      closeActionModal();
      await fetchDocuments();
    } catch (e) {
      showError(extractApiMessage(e, "Failed to update document status."));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo<ResponsiveColumn<DocumentApprovalRow>[]>(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
      },
      {
        id: "documentType",
        header: "Document Type",
        accessor: "documentType",
        sortable: true,
      },
      {
        id: "approvalStatus",
        header: "Status",
        sortable: true,
        render: (item) => (
          <Badge variant={statusBadgeVariant(item.approvalStatus)}>
            {formatStatus(item.approvalStatus)}
          </Badge>
        ),
      },
      {
        id: "pendingFrom",
        header: "Pending From",
        sortable: true,
        nowrap: true,
        render: (item) => formatDisplayDate(item.pendingFrom),
      },
      {
        id: "actions",
        header: "Action",
        nowrap: true,
        render: (item) => (
          <div className={styles.actionGroup}>

            <Button
              size="sm"
              startIcon={<FileCheck size={14} aria-hidden />}
              onClick={() => openActionModal(item)}
            >
              Action
            </Button>
          </div>
        ),
      },
    ],
    [viewLoadingId],
  );

  const modalColumns = useMemo<ResponsiveColumn<DocumentApprovalRow>[]>(
    () => [
      {
        id: "documentType",
        header: "Document Type",
        accessor: "documentType",
      },
      {
        id: "status",
        header: "Status",
        render: (item) => (
          <Badge variant={statusBadgeVariant(item.approvalStatus)}>
            {formatStatus(item.approvalStatus)}
          </Badge>
        ),
      },
      {
        id: "pendingFrom",
        header: "Pending From",
        render: (item) => formatDisplayDate(item.pendingFrom),
      },
      {
        id: "action",
        header: "Action",
        render: (item) => (
          <Button
            variant="muted-outline"
            size="sm"
            startIcon={<Eye size={14} />}
            onClick={() => handleViewDocument(item)}
            disabled={viewLoadingId === item.documentId}
          >
            {viewLoadingId === item.documentId ? "Opening..." : "View"}
          </Button>
        ),
      },
    ],
    [viewLoadingId],
  );

  return (
    <div className={styles.stack}>
      <section className={styles.panel}>

        <div className={styles.filterGrid}>
          <Select
            id="employee-progression-document-type"
            label="Document Type"
            value={documentTypeFilter}
            onChange={setDocumentTypeFilter}
            options={documentTypeOptions}
            searchable
            disabled={documents.length === 0}
          />
          <Select
            id="employee-progression-document-employee"
            label="Employee Name"
            value={employeeFilter}
            onChange={setEmployeeFilter}
            options={employeeOptions}
            searchable
            disabled={employeeOptions.length === 0}
          />
        </div>
      </section>

      <section className={styles.panel}>
        {loading ? (
          <div className={styles.loadingWrap}>
            <ThreeDots height="56" width="56" color="var(--color-accent-500)" visible />
          </div>
        ) : (
          <ResponsiveDataTable
            pagination={false}
            serialNumber
            columns={columns}
            data={filteredDocuments}
            aria-label="Document Approvals"
            emptyState={
              <EmptyStateMessage message="Approval for pending documents is not available." />
            }
          />
        )}
      </section>

      <Modal
        open={selectedDocument != null}
        onClose={closeActionModal}
        title="Verify Document"
        size="lg"
        footer={
          <>
            <div className={styles.modalFooter}>
              <Button variant="light-grey" onClick={closeActionModal}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAction}
                disabled={
                  submitting ||
                  !documentAction ||
                  (documentAction === "rejected" && reason.trim().length < 5)
                }
              >
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </>
        }
      >

        <div className={styles.viewDocumentSection}>
          <ResponsiveDataTable
            columns={modalColumns}
            data={selectedDocument ? [selectedDocument] : []}
            serialNumber={false}
            aria-label="Selected Document"
          />
        </div>
        <div className={styles.modalControls}>
          <Select
            id="employee-progression-document-action"
            label="Action"
            value={documentAction}
            onChange={setDocumentAction}
            options={[
              { value: "approved", label: "Approve" },
              { value: "rejected", label: "Reject" },
            ]}
            placeholder="Select Action"
            required
          />
          {documentAction === "rejected" ? (
            <TextArea
              id="employee-progression-reject-reason"
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              minLength={5}
              maxLength={100}
              showCharacterCount
              required
            />
          ) : null}
        </div>
      </Modal>

      <button
        id="open-document-view"
        type="button"
        data-bs-toggle="modal"
        data-bs-target="#staticBackdropDocumentViewer"
        className={styles.hiddenButton}
        tabIndex={-1}
        aria-hidden
      />
      {documentDetail.url ? (
        <DocumentViewer
          url={documentDetail.url}
          contentType={documentDetail.type}
          fileName={documentDetail.name}
          onClose={clearDocumentDetail}
        />
      ) : null}
    </div>
  );
}
